/**
 * Turn a chosen readout or ceremony into a playlist of clips and captions
 * (docs/TIBBS-VOICE.md 5.6). Pure: the manifest says how each line splits
 * around its names; this module splices in name clips, or drops a missing
 * name and plays the line's nameless form (3.4).
 */
import { CEREMONY_TRIGGERS, LINE_BY_ID, captionOf, namelessCaption } from './library';
import type { Line } from './library';
import type { Ceremony, CeremonyBeat, LinePick, Readout } from './select';

export type NameForm = 'plain' | 'proclaim';

export type ManifestPart = { seg: string } | { name: 1 | 2; form: NameForm } | { gap: number };

export interface VoiceManifest {
  version: number;
  voice: Record<string, unknown>;
  files: Record<string, { file: string; hash: string; dur: number; bytes: number; tier: 'core' | 'extended' }>;
  lines: Record<string, { textHash: string; dur: number; parts: ManifestPart[] }>;
  names: Record<string, { plain: string; proclaim: string }>;
}

/** One step of the playlist. `cue` steps carry no audio: they fire when playback reaches them. */
export type Step =
  | { kind: 'clip'; url: string; dur: number }
  | { kind: 'name'; url: string; seat: number }
  | { kind: 'gap'; ms: number }
  | { kind: 'cue'; cue: Cue };

export interface Cue {
  lineId: string;
  caption: string;
  /** The players this line is about (row highlight). */
  seats: number[];
  pose: Line['pose'];
  /** Ceremony: which beat this line is. */
  beat?: number;
  kind?: CeremonyBeat['kind'];
  /** Ceremony: the seat whose name is being spoken from this step on (the reveal). */
  reveal?: number;
}

/** Where a seat's name clip lives, or null when it is not available (offline and never fetched, no key...). */
export type NameUrl = (seat: number, form: NameForm) => string | null;

export const NAME_GAP_MS = 150;
const BETWEEN_LINES_MS = 250;
const BETWEEN_BEATS_MS = 650;
/** Ceremony with a name we cannot say: a silent beat while the name appears on screen. */
const REVEAL_MS = 700;

function lineSteps(
  m: VoiceManifest,
  line: Line,
  seats: number[],
  nameUrl: NameUrl,
  opts: { reveal: boolean; names: (seat: number) => string; beat?: number; kind?: CeremonyBeat['kind'] },
): Step[] {
  const entry = m.lines[line.id];
  const form: NameForm = CEREMONY_TRIGGERS.has(line.trigger) ? 'proclaim' : 'plain';
  const twoSlots = line.text.includes('{name2}');
  // A single {name} slot shared by several seats (a shared medal): "A. And B."
  const slotSeats = (slot: 1 | 2): number[] => (slot === 2 ? (seats[1] !== undefined ? [seats[1]] : []) : twoSlots ? (seats[0] !== undefined ? [seats[0]] : []) : seats);
  const allSaid = seats.every((s) => nameUrl(s, form) !== null);
  const nameless = !allSaid && !opts.reveal;
  const caption = nameless ? namelessCaption(line) : captionOf(line, captionNames(line, seats, opts.names));
  const out: Step[] = [{ kind: 'cue', cue: { lineId: line.id, caption, seats, pose: line.pose, ...(opts.beat !== undefined ? { beat: opts.beat } : {}), ...(opts.kind ? { kind: opts.kind } : {}) } }];
  if (!entry) return out;
  const andSeg = m.lines['and']?.parts.find((p): p is { seg: string } => 'seg' in p)?.seg;
  for (const part of entry.parts) {
    if ('seg' in part) {
      const f = m.files[part.seg];
      if (f) out.push({ kind: 'clip', url: f.file, dur: f.dur });
    } else if ('gap' in part) {
      out.push({ kind: 'gap', ms: part.gap });
    } else {
      const who = slotSeats(part.name);
      if (nameless) {
        out.push({ kind: 'gap', ms: NAME_GAP_MS });
        continue;
      }
      who.forEach((seat, i) => {
        if (i > 0) {
          out.push({ kind: 'gap', ms: NAME_GAP_MS });
          const f = andSeg ? m.files[andSeg] : undefined;
          if (f) out.push({ kind: 'clip', url: f.file, dur: f.dur });
          out.push({ kind: 'gap', ms: NAME_GAP_MS });
        }
        const url = nameUrl(seat, part.form ?? form);
        if (opts.reveal) out.push({ kind: 'cue', cue: { ...(out[0] as { cue: Cue }).cue, reveal: seat } });
        out.push(url ? { kind: 'name', url, seat } : { kind: 'gap', ms: REVEAL_MS });
      });
    }
  }
  return collapseGaps(out);
}

function captionNames(line: Line, seats: number[], names: (seat: number) => string): string[] {
  if (line.text.includes('{name2}')) return [names(seats[0]!), seats[1] !== undefined ? names(seats[1]) : ''];
  if (seats.length <= 1) return seats.map(names);
  const list = seats.map(names);
  return [`${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`];
}

/** Neighboring gaps merge into the longest one; no gap at either end. */
export function collapseGaps(steps: Step[]): Step[] {
  const out: Step[] = [];
  for (const s of steps) {
    const prev = out[out.length - 1];
    if (s.kind === 'gap' && prev?.kind === 'gap') prev.ms = Math.max(prev.ms, s.ms);
    else out.push(s.kind === 'gap' ? { ...s } : s);
  }
  while (out[out.length - 1]?.kind === 'gap') out.pop();
  const firstAudio = out.findIndex((s) => s.kind !== 'cue');
  if (firstAudio >= 0 && out[firstAudio]!.kind === 'gap') out.splice(firstAudio, 1);
  return out;
}

export function planReadout(m: VoiceManifest, r: Readout, nameUrl: NameUrl, names: (seat: number) => string): Step[] {
  const out: Step[] = [];
  const add = (p: LinePick | null) => {
    if (!p) return;
    const line = LINE_BY_ID.get(p.lineId);
    if (!line) return;
    if (out.length) out.push({ kind: 'gap', ms: BETWEEN_LINES_MS });
    out.push(...lineSteps(m, line, p.seats, nameUrl, { reveal: false, names }));
  };
  add(r.lead);
  add(r.tail);
  return out;
}

export function planCeremony(m: VoiceManifest, c: Ceremony, nameUrl: NameUrl, names: (seat: number) => string): Step[] {
  const out: Step[] = [];
  c.beats.forEach((b, i) => {
    const line = LINE_BY_ID.get(b.lineId);
    if (!line) return;
    if (out.length) out.push({ kind: 'gap', ms: BETWEEN_BEATS_MS });
    out.push(...lineSteps(m, line, b.seats, nameUrl, { reveal: true, names, beat: i, kind: b.kind }));
  });
  return out;
}

/** Every clip URL a playlist needs. */
export function urlsOf(steps: Step[]): string[] {
  return [...new Set(steps.flatMap((s) => (s.kind === 'clip' || s.kind === 'name' ? [s.url] : [])))];
}

/** Seconds a caption stays up with no voice (muted, or captions-only): a comfortable reading pace. */
export function readingTime(caption: string): number {
  const words = caption.split(/\s+/).filter(Boolean).length;
  return Math.max(2.4, words / 3.2);
}

/** All segment files a line needs (for the offline check). */
export function segsOf(m: VoiceManifest, lineId: string): string[] {
  return (m.lines[lineId]?.parts ?? []).flatMap((p) => ('seg' in p ? [p.seg] : []));
}
